import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "@remix-run/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { ClientOnly } from "@core/components/client-only";
import { useApi } from "@core/api";
import { Button } from "@core/components/ui/button";
import { useToast } from "@core/components/ui/use-toast";
import { PagedResultsTypeahead } from "@core/components/PagedResultsTypeahead";
import { User } from "@/apidtos";

const AFFILIATE_ASSOCIATION = "Affiliate";
const CRM_USER_TAG_KEY = "AssignedCRMUserId";

/** Affiliate CSR links are Primary (CSR customer) → Secondary (account). Only one active link per account. */
async function deactivateOtherAffiliateCsrLinks(
  api: ReturnType<typeof useApi>,
  secondaryCustomerId: number,
  newPrimaryCustomerId: number,
) {
  const pageSize = 100;
  for (let pageIndex = 0; ; pageIndex++) {
    const page = await api.ListCustomerAssociation({
      SecondaryId: secondaryCustomerId,
      TypeId: AFFILIATE_ASSOCIATION,
      PageIndex: pageIndex,
      PageSize: pageSize,
    });
    const results = page.Results ?? [];
    for (const association of results) {
      if (
        association.Id
        && association.PrimaryId
        && association.PrimaryId !== newPrimaryCustomerId
      ) {
        await api.UpdateCustomerAssociation({ Id: association.Id, IsActive: false });
      }
    }
    if (results.length < pageSize) {
      break;
    }
  }
}

const CustomerCsrAssignmentInner = ({ customerId }: { customerId: number }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { setAccordionValue, accordionValue } = useEditorFrameContext();
  const sectionTitle = t("ui.crm", "CRM");

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [hasHydratedDefault, setHasHydratedDefault] = useState(false);

  useEffect(() => {
    if (!accordionValue.includes(sectionTitle)) {
      setAccordionValue((prev) => [...prev, sectionTitle]);
    }
  }, [accordionValue, sectionTitle, setAccordionValue]);

  const { data: currentCsrUser } = useQuery({
    queryKey: ["customer-crm-assignment", customerId],
    queryFn: async () => {
      const customer = await api.GetCustomerById(customerId);
      const taggedUserIdRaw = customer.Tags?.find((tag) => tag.Key === CRM_USER_TAG_KEY)?.Value;
      const taggedUserId = taggedUserIdRaw ? Number.parseInt(taggedUserIdRaw, 10) : NaN;
      if (Number.isFinite(taggedUserId)) {
        const taggedUser = await api.GetUserById(taggedUserId);
        if (taggedUser?.Id) {
          return taggedUser;
        }
      }

      const associations = await api.ListCustomerAssociation({
        SecondaryId: customerId,
        TypeId: AFFILIATE_ASSOCIATION,
        PageIndex: 0,
        PageSize: 50,
      });
      if (!associations.Results?.length) {
        return null;
      }

      const primaryIds = [...new Set(
        associations.Results
          .map((association) => association.PrimaryId)
          .filter((id): id is number => !!id)
      )];

      for (const primaryCustomerId of primaryIds) {
        const users = await api.ListUser({
          CustomerId: primaryCustomerId,
          PageIndex: 0,
          PageSize: 50,
        });
        for (const user of users.Results ?? []) {
          const userRoles = await api.GetAppliedRolesForUser(user.Id);
          if (userRoles.includes("CSR")) {
            return user;
          }
        }
      }

      return null;
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!hasHydratedDefault && currentCsrUser) {
      setSelectedUser(currentCsrUser);
      setHasHydratedDefault(true);
    }
  }, [currentCsrUser, hasHydratedDefault]);

  const assignCsrToCustomer = useMutation({
    mutationFn: async (user: User) => {
      let primaryCustomerId = user.CustomerId;
      if (!primaryCustomerId) {
        const loadedUser = await api.GetUserById(user.Id);
        primaryCustomerId = loadedUser.CustomerId;
      }
      if (!primaryCustomerId) {
        throw new Error("Selected user must be assigned to a customer before CRM assignment.");
      }
      if (primaryCustomerId === customerId) {
        throw new Error(
          "CRM user cannot belong to the same customer record as this account; assign a user from the CSR organization.",
        );
      }
      const userRoles = await api.GetAppliedRolesForUser(user.Id);
      if (!userRoles.includes("CSR")) {
        await api.AddRoleToUser(user.Id, "CSR");
      }

      await deactivateOtherAffiliateCsrLinks(api, customerId, primaryCustomerId);

      const existingAssociations = await api.ListCustomerAssociation({
        PrimaryId: primaryCustomerId,
        SecondaryId: customerId,
        TypeId: AFFILIATE_ASSOCIATION,
        PageIndex: 0,
        PageSize: 1,
      });

      const updatePayload: Record<string, unknown> = {
        Id: customerId,
        // Persist exact selected CRM user so refreshes remain deterministic.
        Tags: [{ Key: CRM_USER_TAG_KEY, Value: String(user.Id), "$op": "upsert" }],
      };

      // Must use CustomerAssociatedWith (this customer is Secondary), not CustomerAssociations (Primary).
      // Nesting under CustomerAssociations forces PrimaryId to the edited customer and creates 4→4 rows.
      if ((existingAssociations.Results?.length ?? 0) === 0) {
        updatePayload.CustomerAssociatedWith = [{
          PrimaryId: primaryCustomerId,
          SecondaryId: customerId,
          TypeId: AFFILIATE_ASSOCIATION,
        }];
      }

      await api.UpdateCustomer(updatePayload);
    },
    onSuccess: () => {
      setHasHydratedDefault(true);
      queryClient.invalidateQueries({ queryKey: ["customer-crm-assignment", customerId] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast({
        title: t("ui.crmAssigned", "CRM assigned"),
        description: t(
          "ui.crmAssignedDescription",
          "The selected user was assigned to this customer with CSR role."
        ),
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error
        ? error.message
        : t(
          "ui.crmAssignmentFailedDescription",
          "Unable to assign the selected user. Please try again."
        );
      toast({
        variant: "destructive",
        title: t("ui.crmAssignmentFailed", "CRM assignment failed"),
        description: message,
      });
    },
  });

  return (
    <EditorSection title={sectionTitle}>
      <div className="space-y-4">
        <PagedResultsTypeahead
          key={`crm-typeahead-${selectedUser?.Id ?? "none"}`}
          label={t("ui.crm", "CRM")}
          defaultValue={selectedUser ?? undefined}
          displayFn={(user: User) =>
            [
              user.Contact?.DisplayName ?? user.Contact?.FullName,
              user.UserName,
              user.Email,
            ]
              .filter(Boolean)
              .join(" | ")
          }
          paging={{ PageIndex: 0, PageSize: 20 }}
          onReload={async ({ Query = "", PageIndex = 0, PageSize = 20 }) => {
            const filters = {
              PageIndex,
              PageSize,
              NameContains: Query,
            };
            return await queryClient.fetchQuery({
              queryKey: ["crm-users", Query, PageIndex, PageSize],
              queryFn: () => api.ListUser(filters),
            });
          }}
          onSelect={(user: User) => setSelectedUser(user)}
          onClear={() => setSelectedUser(null)}
        />

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selectedUser
              ? t("ui.selectedUserWithName", "Selected: {{name}}", {
                  name:
                    selectedUser.Contact?.DisplayName
                    ?? selectedUser.Contact?.FullName
                    ?? selectedUser.UserName
                    ?? selectedUser.Email
                    ?? selectedUser.Id,
                })
              : t("ui.selectUserToAssignAsCsr", "Select a user to assign as CSR for this customer.")}
          </p>
          <Button
            type="button"
            disabled={!selectedUser || assignCsrToCustomer.isPending}
            onClick={() => selectedUser && assignCsrToCustomer.mutate(selectedUser)}
          >
            {assignCsrToCustomer.isPending
              ? t("ui.assigning", "Assigning...")
              : t("ui.assignCsr", "Assign CSR")}
          </Button>
        </div>
      </div>
    </EditorSection>
  );
};

const CustomerCsrAssignmentSection = () => {
  const { id } = useParams();
  const customerId = id ? Number(id) : NaN;
  if (!Number.isFinite(customerId)) {
    return null;
  }

  return <CustomerCsrAssignmentInner customerId={customerId} />;
};

const CustomerCsrAssignmentExtension = () => (
  <ClientOnly>
    <CustomerCsrAssignmentSection />
  </ClientOnly>
);

registerExtension(
  "admin.accounts.customers_.$id.additionalSections",
  CustomerCsrAssignmentExtension,
  "CustomerCsrAssignmentExtension",
);
